import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import Select, { MultiValue } from "react-select";
import config from "../../config";
import { authFetch } from "../../utils/Security/authFetch";
import "../../Style/UserManagment.css";

// Mirrors com.bcom.bcom.model.User (note: "organzation" is spelled that way in the backend)
interface User {
  id?: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  organzation: string;
  businessphone: string;
  phonenumber: string;
  costcenter: string;
  location: string;
  position: string;
  status: string;
  dob: string | number | null;
  isAdmin: boolean;
  superadmin: boolean;
  isUser: boolean;
  isemployee: boolean;
  ismanager: boolean;
  superemployee: boolean;
  isForTelebras: boolean;
  isForPetrobras: boolean;
  isForNilesat: boolean;
}

const emptyUser: User = {
  username: "",
  firstname: "",
  lastname: "",
  email: "",
  organzation: "",
  businessphone: "",
  phonenumber: "",
  costcenter: "",
  location: "",
  position: "",
  status: "",
  dob: null,
  isAdmin: false,
  superadmin: false,
  isUser: true,
  isemployee: false,
  ismanager: false,
  superemployee: false,
  isForTelebras: false,
  isForPetrobras: false,
  isForNilesat: false,
};

type TextField = { name: keyof User; label: string; type?: string; required?: boolean };

const textFields: TextField[] = [
  { name: "username", label: "Username", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "firstname", label: "First Name", required: true },
  { name: "lastname", label: "Last Name", required: true },
  { name: "organzation", label: "Organization" },
  { name: "position", label: "Position" },
  { name: "location", label: "Location" },
  { name: "costcenter", label: "Cost Center" },
  { name: "phonenumber", label: "Phone Number", type: "tel" },
  { name: "businessphone", label: "Business Phone", type: "tel" },
  { name: "status", label: "Status" },
  { name: "dob", label: "Date of Birth", type: "date" },
];

const flagFields: { name: keyof User; label: string }[] = [
  { name: "isUser", label: "User" },
  { name: "isAdmin", label: "Admin" },
  { name: "superadmin", label: "Super Admin" },
  { name: "isemployee", label: "Employee" },
  { name: "superemployee", label: "Super Employee" },
  { name: "ismanager", label: "Manager" },
  { name: "isForTelebras", label: "Telebras" },
  { name: "isForPetrobras", label: "Petrobras" },
  { name: "isForNilesat", label: "Nilesat" },
];

type Option = { value: string; label: string };

const roleOptions: Option[] = flagFields.map((f) => ({ value: f.name, label: f.label }));

// The backend PUT only saves these fields; everything else is set at creation time
const editableOnUpdate: (keyof User)[] = [
  "username",
  "firstname",
  "lastname",
  "email",
  "isAdmin",
  "isUser",
];

const toDateInput = (dob: User["dob"]): string => {
  if (dob === null || dob === undefined || dob === "") return "";
  const d = new Date(dob);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState<Option[]>([]);
  const [roleFilter, setRoleFilter] = useState<Option[]>([]);
  const [positionFilter, setPositionFilter] = useState<Option[]>([]);
  const [form, setForm] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const API_URL = `${config.apiBaseUrl}/api/users`;
  const isEdit = form?.id !== undefined;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await authFetch(API_URL);
      if (res.ok) {
        setUsers(await res.json());
      } else {
        setError(`Failed to load users (${res.status})`);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err instanceof Error && err.message ? err.message : "Could not reach the server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Distinct, sorted values for the filter dropdowns (case/whitespace-insensitive)
  const distinctValues = (field: "organzation" | "position") => {
    const seen = new Map<string, string>();
    users.forEach((u) => {
      const v = (u[field] ?? "").trim();
      if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
    });
    return Array.from(seen.values())
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }));
  };
  const organizations = useMemo(() => distinctValues("organzation"), [users]); // eslint-disable-line react-hooks/exhaustive-deps
  const positions = useMemo(() => distinctValues("position"), [users]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersActive = Boolean(
      search || orgFilter.length || roleFilter.length || positionFilter.length,
  );

  const clearFilters = () => {
    setSearch("");
    setOrgFilter([]);
    setRoleFilter([]);
    setPositionFilter([]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Within one filter the selected values are OR'ed; across filters they are AND'ed
    const matches = (value: string | undefined, selected: Option[]) => {
      const v = (value ?? "").trim().toLowerCase();
      return !selected.length || selected.some((o) => o.value.toLowerCase() === v);
    };
    return users.filter(
        (u) =>
            matches(u.organzation, orgFilter) &&
            matches(u.position, positionFilter) &&
            (!roleFilter.length || roleFilter.some((r) => Boolean(u[r.value as keyof User]))) &&
            (!q ||
                [u.username, u.firstname, u.lastname, u.email, u.organzation, u.position]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q))),
    );
  }, [users, search, orgFilter, roleFilter, positionFilter]);

  const openAdd = () => {
    setError(null);
    setForm({ ...emptyUser });
  };

  const openEdit = (user: User) => {
    setError(null);
    // Backend may return nulls; fill them so inputs stay controlled
    const filled = { ...emptyUser } as any;
    Object.entries(user).forEach(([k, v]) => {
      if (v !== null && v !== undefined) filled[k] = v;
    });
    setForm({ ...filled, dob: toDateInput(user.dob) });
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) =>
        prev ? { ...prev, [name]: type === "checkbox" ? checked : value } : prev,
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, dob: form.dob ? form.dob : null };
      const res = await authFetch(isEdit ? `${API_URL}/${form.id}` : API_URL, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setNotice(isEdit ? "User updated" : "User created");
        setForm(null);
        fetchUsers();
      } else {
        setError(`Save failed (${res.status})`);
      }
    } catch (err) {
      console.error("Error saving user:", err);
      setError(err instanceof Error && err.message ? err.message : "Could not reach the server");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user: User) => {
    const name = `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim() || user.username;
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API_URL}/${user.id}`, { method: "DELETE" });
      if (res.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
        setNotice("User deleted");
      } else {
        setError(`Delete failed (${res.status})`);
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(err instanceof Error && err.message ? err.message : "Could not reach the server");
    }
  };

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  const locked = (name: keyof User) => isEdit && !editableOnUpdate.includes(name);

  return (
      <div className="um-page">
        <div className="um-header">
          <h1>User Management</h1>
          <div className="um-toolbar">
            <input
                className="um-search"
                type="search"
                placeholder="Search name, email, organization…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <button className="um-btn um-btn-primary" onClick={openAdd}>
              + Add User
            </button>
          </div>
        </div>

        <div className="um-filters">
          <Select
              isMulti
              className="um-select"
              classNamePrefix="um-select"
              placeholder="All organizations"
              options={organizations}
              value={orgFilter}
              onChange={(v: MultiValue<Option>) => setOrgFilter([...v])}
              closeMenuOnSelect={false}
              aria-label="Filter by organization"
          />
          <Select
              isMulti
              className="um-select"
              classNamePrefix="um-select"
              placeholder="All roles"
              options={roleOptions}
              value={roleFilter}
              onChange={(v: MultiValue<Option>) => setRoleFilter([...v])}
              closeMenuOnSelect={false}
              aria-label="Filter by role"
          />
          <Select
              isMulti
              className="um-select"
              classNamePrefix="um-select"
              placeholder="All positions"
              options={positions}
              value={positionFilter}
              onChange={(v: MultiValue<Option>) => setPositionFilter([...v])}
              closeMenuOnSelect={false}
              aria-label="Filter by position"
          />
          {filtersActive && (
              <button className="um-btn um-btn-small" onClick={clearFilters}>
                Clear filters
              </button>
          )}
          <span className="um-count">
          {loading ? "" : `${filtered.length} of ${users.length} users`}
        </span>
        </div>

        {notice && <div className="um-alert um-alert-success">{notice}</div>}
        {error && !form && <div className="um-alert um-alert-error">{error}</div>}

        <div className="um-table-wrap">
          <table className="um-table">
            <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Organization</th>
              <th>Position</th>
              <th>Roles</th>
              <th className="um-actions-col">Actions</th>
            </tr>
            </thead>
            <tbody>
            {loading && (
                <tr>
                  <td colSpan={7} className="um-empty">Loading users…</td>
                </tr>
            )}
            {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="um-empty">
                    {filtersActive ? "No users match your filters" : "No users yet"}
                  </td>
                </tr>
            )}
            {!loading &&
                filtered.map((u) => (
                    <tr key={u.id}>
                      <td>{`${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || "—"}</td>
                      <td>{u.username || "—"}</td>
                      <td>{u.email || "—"}</td>
                      <td>{u.organzation || "—"}</td>
                      <td>{u.position || "—"}</td>
                      <td>
                        <div className="um-badges">
                          {flagFields
                              .filter((f) => u[f.name])
                              .map((f) => (
                                  <span key={f.name} className="um-badge">{f.label}</span>
                              ))}
                        </div>
                      </td>
                      <td className="um-actions-col">
                        <button className="um-btn um-btn-small" onClick={() => openEdit(u)}>
                          Edit
                        </button>
                        <button
                            className="um-btn um-btn-small um-btn-danger"
                            onClick={() => handleDelete(u)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                ))}
            </tbody>
          </table>
        </div>

        {form && (
            <div className="um-overlay" onClick={() => !saving && setForm(null)}>
              <form
                  className="um-modal"
                  onClick={(e) => e.stopPropagation()}
                  onSubmit={handleSubmit}
              >
                <h2>{isEdit ? "Edit User" : "Add User"}</h2>
                {isEdit && (
                    <p className="um-hint">
                      Greyed-out fields can only be set when the user is created.
                    </p>
                )}
                {error && <div className="um-alert um-alert-error">{error}</div>}

                <div className="um-grid">
                  {textFields.map((f) => (
                      <label key={f.name} className="um-field">
                  <span>
                    {f.label}
                    {f.required && " *"}
                  </span>
                        <input
                            type={f.type ?? "text"}
                            name={f.name}
                            value={(form[f.name] as string) ?? ""}
                            onChange={handleChange}
                            required={f.required}
                            disabled={locked(f.name)}
                        />
                      </label>
                  ))}
                </div>

                <h3>Roles &amp; Access</h3>
                <div className="um-flags">
                  {flagFields.map((f) => (
                      <label key={f.name} className={`um-flag ${locked(f.name) ? "um-disabled" : ""}`}>
                        <input
                            type="checkbox"
                            name={f.name}
                            checked={Boolean(form[f.name])}
                            onChange={handleChange}
                            disabled={locked(f.name)}
                        />
                        {f.label}
                      </label>
                  ))}
                </div>

                <div className="um-modal-actions">
                  <button
                      type="button"
                      className="um-btn"
                      onClick={() => setForm(null)}
                      disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="um-btn um-btn-primary" disabled={saving}>
                    {saving ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
                  </button>
                </div>
              </form>
            </div>
        )}
      </div>
  );
};

export default UserManagement;
